import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { Table } from 'reactstrap';
import { Button, Carousel, Spinner } from 'react-bootstrap';
import BasePage from '../../../BasePage/BasePage';
import LiveInput from '../../../../atoms/LiveInput/LiveInput';
import Icon from '../../../../atoms/Icon/Icon';
import Sidebar from '../../../../molecules/Sidebar/Sidebar';
import { loadElementsMetadata } from '../../../../../helpers/metadata';
import DeleteModal from '../../../../molecules/DeleteModal/DeleteModal';
import Api from '../../../../../services/Api';
import { fetchElementsInitAction, updateSelectedElementAction } from '../../../../../actions/instances/elements';
import { updateSelectedComponentAction } from '../../../../../actions/instances/components';
import DocumentsList from '../../../../Organisms/DocumentsList/DocumentsList';
import InstanceMetadata from '../../../../Organisms/InstanceMetadata/InstanceMetadata';
import BuildingsList from '../../../../Organisms/BuildingsList/BuildingsList';
import CitationList from '../../../../Organisms/CitationList/CitationList';
import { publishElement } from '../../../../../helpers/publish';
import CharacteristicsCardList from '../../../../Organisms/CharacteristicsCardList/CharacteristicsCardList';
import ContextsList from '../../../../Organisms/ContextsList/ContextsList';
import './EditViewComponent.scss';

const EditViewElement = () => {
  const {
    selectedElement,
  } = useSelector(state => state.buildingElementInstance);
  const { user } = useSelector(state => state.auth);
  const history = useHistory();
  const [components, setComponents] = useState([]);
  const [characteristics, setCharacteristics] = useState([]);
  const [contexts, setContexts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalShown, setIsDeleteModalShown] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const loadMetadata = async () => {
    const metadata = await loadElementsMetadata(selectedElement.id);
    setComponents(metadata.components);
    setCharacteristics(metadata.characteristics);
    setContexts(metadata.contexts);
  };
  useEffect(() => {
    if (selectedElement) {
      setIsLoading(true);
      loadMetadata().then(() => setIsLoading(false));
    }
  }, []);
  if (!selectedElement) {
    history.push('/instances/building-elements');
  }
  const dispatch = useDispatch();
  const deleteElement = () => {
    setIsDeleting(true);
    Api.delete(`/instances/building-elements/${selectedElement.id}`).then((response) => {
      if (response.status === 204) {
        dispatch(fetchElementsInitAction());
        setTimeout(() => {
          history.goBack();
          setIsDeleting(false);
        }, 500);
      } else {
        setIsDeleting(false);
      }
    });
  };
  const syncCharacteristics = async (newCharacteristic) => {
    setIsLoading(true);
    const componentCharacteristic = await Api.get(`instances/building-elements/${selectedElement.id}`);
    const newIds = [
      ...componentCharacteristic.data.characteristics.map(x => x.id),
      newCharacteristic.id,
    ];
    await Api.patch(`instances/building-elements/${selectedElement.id}`, {
      characteristics_ids: newIds,
    });
    const response = await loadElementsMetadata(selectedElement.id);
    setComponents(response.elements);
    setCharacteristics(response.characteristics);
    setIsLoading(false);
  };
  const syncContexts = async (newContext) => {
    setIsLoading(true);
    const ids = contexts.map(x => x.id);
    await Api.patch(`instances/building-elements/${selectedElement.id}`, {
      contexts_ids: [...ids, newContext.id],
    });
    await loadMetadata();
    setIsLoading(false);
  };
  return (
    <BasePage>
      <DeleteModal
        onDelete={deleteElement}
        isShown={isDeleteModalShown}
        isLoading={isDeleting}
        onClose={() => setIsDeleteModalShown(false)}
        isAllowed={components ? components.length === 0 : true}
      >
        Are you sure you want to delete
        {' '}
        <strong>{selectedElement.name}</strong>
        {'? '}
        <br />
        This action cannot be undone.
      </DeleteModal>
      <div className="col-md-3">
        <div className="row">
          <div className="col-md-12">
            {selectedElement.images && selectedElement.images.length > 0 ? (
              <Carousel>
                {selectedElement.images.map(image => (
                  <Carousel.Item>
                    <img
                      className="d-block w-100 img-fluid"
                      src={image.url}
                      alt={image.label}
                      style={{
                        maxHeight: '400px',
                        objectFit: 'cover',
                      }}
                    />
                  </Carousel.Item>
                ))}
              </Carousel>
            ) : (
              <img src="/images/default.jpg" alt={selectedElement.name} className="img-fluid" />
            )}
          </div>
          <div className="col-md-12 mt-2 d-grid gap-2">
            {user.role_id !== 3 && (
              <Button
                variant={selectedElement.in_review === 1 ? 'success' : 'warning'}
                onClick={async () => {
                  setIsPublishing(true);
                  const component = await publishElement(
                    selectedElement.id,
                    selectedElement.in_review,
                  );
                  dispatch(updateSelectedElementAction(component));
                  dispatch(fetchElementsInitAction());
                  setIsPublishing(false);
                }}
              >
                {isPublishing ? (
                  <>
                    <Spinner
                      as="span"
                      animation="grow"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    <span className="ms-2">Loading...</span>
                  </>
                ) : (
                  <>
                    <Icon icon="check" />
                    <span className={`ms-2 ${selectedElement.in_review === 0 ? 'text-white' : ''}`}>
                      {selectedElement.in_review === 1 ? 'Approve' : 'Disapprove'}
                    </span>
                  </>
                )}
              </Button>
            )}
            <Button
              variant="dark"
              onClick={() => {
                dispatch(updateSelectedElementAction(selectedElement));
                history.push('/instances/building-elements/edit');
              }}
            >
              <Icon icon="edit" />
              <span className="ms-2">Edit</span>
            </Button>
            <Button variant="dark" onClick={() => history.goBack()}>
              <Icon icon="times" />
              <span className="ms-2">Cancel</span>
            </Button>
            {user.role_id !== 3 && (
              <Button variant="danger" onClick={() => setIsDeleteModalShown(true)}>
                {isDeleting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="grow"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    <span className="ms-2">Loading...</span>
                  </>
                ) : (
                  <>
                    <Icon icon="trash" />
                    <span className="ms-2">Delete</span>
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="col-md-6">
        <div className="row d-flex align-items-center justify-content-center">
          <div className="col-md-1 p-0 text-end">
            <Icon icon="edit" color="#000" />
          </div>
          <div className="col-md-11 p-0 text-start ps-2">
            <LiveInput endpoint="/instances/building-elements" fieldName="name" id={selectedElement.id} value={selectedElement.name} fontSize={22} />
          </div>
        </div>
        <div className="row border-top border-bottom mt-2 p-2">
          <div className="col-md-12">
            <div className="border-end d-inline pe-2 me-2">
              <span className="me-1">
                <Icon icon="calendar" color="#000" />
              </span>
              {moment(selectedElement.cerated_at).format('DD-MM-YYYY')}
            </div>
            <div className="d-inline">
              {selectedElement.tags.map(tag => (
                <span className="me-2 border-end pe-2">
                  <Icon icon="tag" color="#000" />
                  <span className="ms-2">
                    {tag.name}
                  </span>
                </span>
              ))}
            </div>
            <div className="d-inline border-end d-inline pe-2 me-2">
              <Icon icon="map" color="#000" />
              <span className="me-2">
                {`${selectedElement.city.name}, ${selectedElement.city.country.name} [${selectedElement.city.code}-${selectedElement.city.country.code}]`}
              </span>
            </div>
            <div className="d-inline">
              <Icon icon="globe" color="#000" />
              <span className="ms-2">
                {`${selectedElement.dynasty.name} [${selectedElement.dynasty.code}]`}
              </span>
            </div>
          </div>
        </div>

        <InstanceMetadata instance={selectedElement} title="Element Metadata" />

        <BuildingsList buildings={selectedElement.buildings} />
        <div className="row mt-5 mb-5">
          <h6>Attached to</h6>
          <Table responsive striped hover>
            <thead>
              <tr>
                <td />
                <td>Component</td>
                <td>Dynasty</td>
              </tr>
            </thead>
            <tbody>
              {components && components.length > 0 ? (
                components.map(component => (
                  <tr>
                    <td>
                      {component.images.length > 0 ? (
                        <img alt={component.name} src={component.images[0].url} className="img-fluid" height={50} width={50} />
                      ) : (
                        <img alt={component.name} src="/images/default.jpg" className="img-fluid" height={50} width={50} />
                      )}
                    </td>
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                    <td
                      onClick={() => {
                        dispatch(updateSelectedComponentAction(component));
                        history.push('/instances/building-components/view');
                        window.scrollTo(0, 0);
                      }}
                    >
                      {`${component.name} [${component.code}]`}
                    </td>
                    <td>{`${component.dynasty.name} [${component.dynasty.code}]`}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="text-center">This element is not attached to any component</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
        <ContextsList
          contexts={contexts}
          isLoading={isLoading}
          onItemAdded={con => syncContexts(con)}
          cityId={selectedElement.city.id}
          dynastyId={selectedElement.dynasty.id}
        />
        <DocumentsList documents={selectedElement ? selectedElement.documents : []} title="Documents" />
        <CitationList citations={selectedElement ? selectedElement.citation : []} title="Citations" />
      </div>
      <div className="col-md-3 mt-5">
        <CharacteristicsCardList
          characteristics={characteristics}
          cityId={selectedElement.city.id}
          dynastyId={selectedElement.dynasty.id}
          isLoading={isLoading}
          onItemAdded={char => syncCharacteristics(char)}
        />
        <Sidebar />
      </div>
    </BasePage>
  );
};

export default EditViewElement;
